// Panneau lateral « Navigateur » (02/10) : le membre regarde en direct le navigateur que pilote
// l'assistant, et peut « prendre la main » (captcha, connexion a un site). Portage fidele de
// browser-live-panel.tsx de l'ancienne app Pirouette (supprime par 2f07a5e5), sur le systeme de
// conception Sparkle : barre de navigation + adresse + pastille « en direct », vue en direct
// (images JPEG peintes sur un canevas a la taille deviceWidth x deviceHeight), barre du bas
// (narration ambre quand l'assistant agit, bleue quand le membre a la main).
// Regarder, naviguer et controler sont trois choses distinctes : naviguer (fleches, rechargement,
// barre d'adresse) reste toujours permis, seul le clavier-souris demande de prendre la main.

import { useConversationSidePanelContext } from "@app/components/assistant/conversation/ConversationSidePanelContext";
import { BarreAdresseNavigateur } from "@app/components/compta/navigateur/BarreAdresseNavigateur";
import {
  useFluxNavigateur,
  useSaisieNavigateur,
  useSessionNavigateur,
} from "@app/components/compta/navigateur/useNavigateur";
import { AppLayoutTitle } from "@app/components/sparkle/AppLayoutTitle";
import { imageMascotte } from "@app/lib/compta/icones_outils";
import type { VisiteNavigateur } from "@app/lib/compta/navigateur/adresse";
import { ajouterVisite, hoteDe } from "@app/lib/compta/navigateur/adresse";
import type { GesteNavigateur } from "@app/lib/compta/navigateur/libelles";
import {
  LIBELLES_NAVIGATEUR as L,
  libelleGeste,
} from "@app/lib/compta/navigateur/libelles";
import { registreNavigateur } from "@app/lib/compta/navigateur/ouverture";
import {
  evenementClavier,
  evenementMolette,
  evenementSouris,
  versEcranDistant,
} from "@app/lib/compta/navigateur/saisie";
import { classNames } from "@app/lib/utils";
import {
  ArrowLeft,
  ArrowRight,
  Button,
  Edit04,
  Eye,
  Globe01,
  Icon,
  LogIn01,
  Monitor01,
  RefreshCw01,
  Spinner,
  Stars02,
  XClose,
} from "@dust-tt/sparkle";
import type React from "react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

// Fleche BLANCHE a contour noir, pointe en (4,2), quel que soit le systeme : ni viseur, ni
// fleche noire invisible sur une page sombre (reprise telle quelle de l'ancienne app).
const CURSEUR_FLECHE_BLANCHE =
  'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBkPSJNNCAyIEw0IDE4LjUgTDguMiAxNC40IEwxMSAyMC40IEwxMy41IDE5LjIgTDEwLjggMTMuNCBMMTYgMTMuNCBaIiBmaWxsPSIjZmZmZmZmIiBzdHJva2U9IiMwMDAwMDAiIHN0cm9rZS13aWR0aD0iMS4zIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+PC9zdmc+") 4 2, default';

// Heuristique assumee (ancienne app) : le flux ne dit pas quand une page a fini de charger ; la
// roue s'eteint quand l'adresse change, et au plus tard apres ce delai.
const DELAI_ROUE_NAVIGATION_MS = 8000;
/** Fenetre pendant laquelle le dernier geste de l'assistant colore la barre du bas. */
const FENETRE_ASSISTANT_ACTIF_MS = 15_000;

const CLASSE_BOUTON_NAV =
  "rounded p-1 text-muted-foreground enabled:hover:bg-muted-background enabled:hover:text-foreground disabled:opacity-40 dark:text-muted-foreground-night dark:enabled:hover:bg-muted-background-night dark:enabled:hover:text-foreground-night";

// Ecran sans image : fond de la conversation (bg-panel-background, suit le theme) et grille fine
// tracee dans la couleur du texte (--color-foreground, claire en sombre) a 7 %, estompee vers
// les bords par un masque radial.
const COULEUR_GRILLE =
  "color-mix(in srgb, var(--color-foreground) 7%, transparent)";
const MASQUE_GRILLE =
  "radial-gradient(ellipse at center, black 30%, transparent 75%)";
const STYLE_GRILLE: React.CSSProperties = {
  backgroundImage: `linear-gradient(to right, ${COULEUR_GRILLE} 1px, transparent 1px), linear-gradient(to bottom, ${COULEUR_GRILLE} 1px, transparent 1px)`,
  backgroundSize: "72px 72px",
  backgroundPosition: "center center",
  maskImage: MASQUE_GRILLE,
  WebkitMaskImage: MASQUE_GRILLE,
};

const ICONE_GESTE: Record<
  GesteNavigateur,
  React.ComponentType<{ className?: string }>
> = {
  open: Globe01,
  click: Monitor01,
  type: Edit04,
  read: Eye,
  login: LogIn01,
  work: Stars02,
};

function useGesteRecent(conversationId: string) {
  const geste = useSyncExternalStore(
    registreNavigateur.abonner,
    () => registreNavigateur.geste(conversationId),
    () => null
  );
  const [maintenant, setMaintenant] = useState(() => Date.now());
  useEffect(() => {
    setMaintenant(Date.now());
    if (!geste) {
      return;
    }
    const minuteur = setTimeout(
      () => setMaintenant(Date.now()),
      FENETRE_ASSISTANT_ACTIF_MS + 100
    );
    return () => clearTimeout(minuteur);
  }, [geste]);
  return geste && maintenant - geste.a <= FENETRE_ASSISTANT_ACTIF_MS
    ? geste
    : null;
}

export function PanneauNavigateur({
  conversationId,
}: {
  conversationId: string;
}) {
  const { closePanel } = useConversationSidePanelContext();
  const session = useSessionNavigateur(conversationId);
  const { image, statut, url, titre } = useFluxNavigateur(session);
  const adresseAffichee = url ?? "";
  const [main, setMain] = useState(false);
  const canevas = useRef<HTMLCanvasElement>(null);
  const dernierPoint = useRef<{ x: number; y: number } | null>(null);
  const saisie = useSaisieNavigateur(session?.inputUrl ?? null, () =>
    setMain(false)
  );
  const peutControler = statut === "actif";

  // Pages vues par l'assistant : la seule source de ce qui s'est vraiment AFFICHE est l'adresse
  // du flux (une ouverture peut rediriger ou echouer).
  const [visites, setVisites] = useState<VisiteNavigateur[]>([]);
  useEffect(() => {
    if (adresseAffichee) {
      setVisites((v) =>
        ajouterVisite(v, { url: adresseAffichee, titre, a: Date.now() })
      );
    }
  }, [adresseAffichee, titre]);
  const pagesRecentes = visites.slice(-3).reverse();

  const geste = useGesteRecent(conversationId);
  const libelleAssistant = geste ? libelleGeste(geste.geste, geste.url) : null;
  const IconeGeste = geste ? ICONE_GESTE[geste.geste] : null;

  // Peinture des images sur le canevas, a la taille reelle de l'ecran distant.
  useEffect(() => {
    if (!image || !canevas.current) {
      return;
    }
    const cible = canevas.current;
    const img = new Image();
    let annule = false;
    img.onload = () => {
      if (annule) {
        return;
      }
      if (cible.width !== image.largeur || cible.height !== image.hauteur) {
        cible.width = image.largeur;
        cible.height = image.hauteur;
      }
      cible
        .getContext("2d")
        ?.drawImage(img, 0, 0, image.largeur, image.hauteur);
    };
    img.src = `data:image/jpeg;base64,${image.donnees}`;
    return () => {
      annule = true;
    };
  }, [image]);

  const [chargementNav, setChargementNav] = useState(false);
  const adresseAvantNav = useRef("");
  const naviguer = useCallback(
    (action: "back" | "forward" | "reload" | "url", cible?: string) => {
      adresseAvantNav.current = adresseAffichee;
      setChargementNav(true);
      saisie.envoyer(
        action === "url"
          ? { kind: "navigate", action, url: cible }
          : { kind: "navigate", action }
      );
    },
    [adresseAffichee, saisie]
  );
  useEffect(() => {
    if (!chargementNav) {
      return;
    }
    if (adresseAffichee !== adresseAvantNav.current) {
      setChargementNav(false);
      return;
    }
    const minuteur = setTimeout(
      () => setChargementNav(false),
      DELAI_ROUE_NAVIGATION_MS
    );
    return () => clearTimeout(minuteur);
  }, [chargementNav, adresseAffichee]);

  // Session finie : la main revient a l'assistant.
  useEffect(() => {
    if (statut === "terminee" || statut === "nonPrisEnCharge") {
      setMain(false);
    }
  }, [statut]);

  const pointDe = (e: React.PointerEvent | React.WheelEvent) => {
    const c = canevas.current;
    if (!c) {
      return null;
    }
    const r = c.getBoundingClientRect();
    return versEcranDistant({
      clientX: e.clientX,
      clientY: e.clientY,
      rect: { left: r.left, top: r.top, width: r.width, height: r.height },
      ecran: { width: c.width, height: c.height },
    });
  };

  const surAppui = (e: React.PointerEvent) => {
    if (!main) {
      return;
    }
    const p = pointDe(e);
    if (!p) {
      return;
    }
    canevas.current?.focus();
    e.preventDefault();
    canevas.current?.setPointerCapture(e.pointerId);
    dernierPoint.current = p;
    saisie.envoyer(evenementSouris("down", p, e));
  };
  const surDeplacement = (e: React.PointerEvent) => {
    // Seulement pendant un glisser, comme l'ancienne app : pas de flot de survols.
    if (!main || e.buttons === 0) {
      return;
    }
    const p = pointDe(e);
    if (!p) {
      return;
    }
    dernierPoint.current = p;
    saisie.envoyer(evenementSouris("move", p, e));
  };
  const surRelache = (e: React.PointerEvent) => {
    if (!main) {
      return;
    }
    const p = pointDe(e) ?? dernierPoint.current;
    if (!p) {
      return;
    }
    saisie.envoyer(evenementSouris("up", p, e));
    try {
      canevas.current?.releasePointerCapture(e.pointerId);
    } catch {
      // Pointeur non capture.
    }
    dernierPoint.current = null;
  };
  const surMolette = (e: React.WheelEvent) => {
    if (!main) {
      return;
    }
    const p = pointDe(e);
    if (p) {
      saisie.envoyer(evenementMolette(p, e));
    }
  };
  const surTouche = (type: "down" | "up") => (e: React.KeyboardEvent) => {
    if (!main) {
      return;
    }
    e.preventDefault();
    saisie.envoyer(evenementClavier(type, e));
  };

  const rendreLaMain = () => setMain(false);
  const fermer = () => {
    setMain(false);
    closePanel();
  };

  const avecImage = image !== null && statut !== "terminee";

  return (
    <div className="flex h-panel flex-col bg-panel-background">
      <AppLayoutTitle>
        <div className="flex h-full items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <Icon
              visual={Globe01}
              size="xs"
              className="text-muted-foreground"
            />
            <span className="truncate text-sm font-semibold text-foreground dark:text-foreground-night">
              {L.titre}
            </span>
            {statut === "actif" && (
              <span className="flex shrink-0 items-center gap-1 px-1 text-[11px] font-medium text-success-600">
                <span className="size-1.5 rounded-full bg-success-500" />{" "}
                {L.enDirect}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={fermer}
            icon={XClose}
            tooltip={L.fermer}
          />
        </div>
      </AppLayoutTitle>

      <div className="flex shrink-0 items-center gap-1.5 border-b border-border px-2 py-1.5 dark:border-border-night">
        <div className="flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            disabled={!adresseAffichee || statut === "terminee"}
            title={L.precedent}
            aria-label={L.precedent}
            onClick={() => naviguer("back")}
            className={CLASSE_BOUTON_NAV}
          >
            <Icon visual={ArrowLeft} size="xs" />
          </button>
          <button
            type="button"
            disabled={!adresseAffichee || statut === "terminee"}
            title={L.suivant}
            aria-label={L.suivant}
            onClick={() => naviguer("forward")}
            className={CLASSE_BOUTON_NAV}
          >
            <Icon visual={ArrowRight} size="xs" />
          </button>
          <button
            type="button"
            disabled={!adresseAffichee || statut === "terminee"}
            title={L.recharger}
            aria-label={L.recharger}
            onClick={() => naviguer("reload")}
            className={CLASSE_BOUTON_NAV}
          >
            <Icon visual={RefreshCw01} size="2xs" />
          </button>
        </div>
        <BarreAdresseNavigateur
          url={adresseAffichee}
          titre={titre}
          chargement={chargementNav}
          assistantActif={libelleAssistant !== null}
          visites={visites}
          surNavigation={(cible) => naviguer("url", cible)}
        />
      </div>

      <div
        data-testid="navigateur-vue"
        className={classNames(
          "relative flex min-h-0 flex-1 flex-col justify-center overflow-hidden",
          avecImage ? "bg-gray-900" : "bg-panel-background"
        )}
      >
        {!avecImage && (
          <div
            data-testid="navigateur-grille"
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={STYLE_GRILLE}
          />
        )}
        <canvas
          ref={canevas}
          aria-label={L.vueEnDirect}
          role="img"
          tabIndex={main ? 0 : -1}
          onPointerDown={surAppui}
          onPointerMove={surDeplacement}
          onPointerUp={surRelache}
          onWheel={surMolette}
          onKeyDown={surTouche("down")}
          onKeyUp={surTouche("up")}
          onContextMenu={(e) => {
            if (main) {
              e.preventDefault();
            }
          }}
          style={main ? { cursor: CURSEUR_FLECHE_BLANCHE } : undefined}
          className={classNames(
            "max-h-full w-full shrink-0 object-contain outline-none",
            main ? "ring-2 ring-highlight-500" : "",
            avecImage ? "" : "hidden"
          )}
        />
        {!avecImage && (
          <div className="relative flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-6 py-10 text-center text-sm text-muted-foreground dark:text-muted-foreground-night">
            {statut === "connexion" || statut === "actif" ? (
              <span className="flex items-center gap-2">
                <Spinner size="xs" /> {L.connexion}
              </span>
            ) : (
              <>
                <img
                  data-testid="navigateur-mascotte"
                  src={imageMascotte()}
                  alt=""
                  aria-hidden="true"
                  className="size-14 select-none object-contain"
                  draggable={false}
                />
                <span>
                  {statut === "terminee"
                    ? L.terminee
                    : statut === "nonPrisEnCharge"
                      ? L.nonPrisEnCharge
                      : statut === "indisponible"
                        ? L.indisponible
                        : statut === "bloque"
                          ? L.bloque
                          : L.sansSession}
                </span>
                {statut === "terminee" && (
                  <span className="text-xs text-faint dark:text-faint-night">
                    {L.termineeAide}
                  </span>
                )}
                {statut !== "terminee" && pagesRecentes.length > 0 && (
                  <div className="flex w-full max-w-sm flex-col items-stretch gap-1 pt-2">
                    <span className="px-2 text-[10px] font-medium tracking-wide text-faint uppercase dark:text-faint-night">
                      {L.pagesRecentes}
                    </span>
                    {pagesRecentes.map((v) => (
                      <button
                        key={`${v.url}-${v.a}`}
                        type="button"
                        onClick={() => naviguer("url", v.url)}
                        className="min-w-0 truncate rounded px-2 py-1 text-left text-xs text-muted-foreground hover:bg-muted-background hover:text-foreground dark:text-muted-foreground-night dark:hover:bg-muted-background-night dark:hover:text-foreground-night"
                      >
                        {v.titre ?? (hoteDe(v.url) || v.url)}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {libelleAssistant && IconeGeste && !main && avecImage && (
          <div className="pointer-events-none absolute right-3 bottom-3 z-20 flex max-w-[70%] items-center gap-1.5 rounded-full bg-warning-500/15 px-2.5 py-1 text-[11px] font-medium text-warning-300 ring-1 ring-warning-400/30 backdrop-blur-sm">
            <IconeGeste className="size-3 shrink-0" />
            <span className="truncate">{libelleAssistant}</span>
          </div>
        )}
      </div>

      {main ? (
        <div className="flex shrink-0 items-center justify-between gap-1.5 border-t border-border bg-highlight-50 px-3 py-1.5 text-[11px] font-medium text-highlight-700 dark:border-border-night dark:bg-highlight-950 dark:text-highlight-200">
          <span className="flex items-center gap-1.5">
            <Icon visual={Monitor01} size="2xs" /> {L.tuAsLaMain}
          </span>
          <Button
            size="xs"
            variant="outline"
            label={L.rendreLaMain}
            onClick={rendreLaMain}
          />
        </div>
      ) : (
        // Ambre pendant que l'assistant agit (« c'est lui »), bleu quand le membre a la main
        // (« c'est vous »), neutre le reste du temps.
        <div
          className={classNames(
            "flex shrink-0 items-center justify-between gap-1.5 border-t border-border px-3 py-1.5 text-[11px] dark:border-border-night",
            libelleAssistant
              ? "bg-warning-50 font-medium text-warning-800 dark:bg-warning-950 dark:text-warning-200"
              : "bg-muted-background text-muted-foreground dark:bg-muted-background-night dark:text-muted-foreground-night"
          )}
        >
          <span className="truncate">
            {statut === "terminee"
              ? L.terminee
              : (libelleAssistant ?? L.piloteParLAssistant)}
          </span>
          <Button
            size="xs"
            variant="highlight"
            label={L.prendreLaMain}
            tooltip={L.prendreLaMainAide}
            disabled={!peutControler}
            onClick={() => {
              saisie.reinitialiser();
              setMain(true);
              canevas.current?.focus();
            }}
          />
        </div>
      )}
    </div>
  );
}
